import React from 'react';
import { Check } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const VenuePackages = React.memo(({ venue, selectedPackage, setSelectedPackage }) => {
  if (!venue.packages || venue.packages.length === 0) return null;

  const lowestPriceIndex = venue.packages.reduce((minIdx, pkg, idx, arr) => {
    const currentPrice = pkg.price || Infinity;
    const minPrice = arr[minIdx]?.price || Infinity;
    return currentPrice < minPrice ? idx : minIdx;
  }, 0);

  const highestPriceIndex = venue.packages.reduce((maxIdx, pkg, idx, arr) => {
    const currentPrice = pkg.price || 0;
    const maxPrice = arr[maxIdx]?.price || 0;
    return currentPrice > maxPrice ? idx : maxIdx;
  }, 0);

  return (
    <div className="relative">
      <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-3 rounded-2xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Paketler & Fiyatlar</h2>
              <p className="text-sm text-gray-500">Size özel {venue.packages.length} farklı paket seçeneği</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {venue.packages.map((pkg, index) => (
              <div 
                key={index}
                className={`relative group rounded-2xl p-6 transition-all duration-300 border-2 cursor-pointer ${
                  selectedPackage?.name === pkg.name 
                    ? 'border-pink-500 bg-gradient-to-br from-pink-50 to-purple-50 shadow-xl scale-[1.02]' 
                    : 'border-gray-200 bg-white hover:border-pink-300 hover:shadow-lg'
                }`}
                onClick={() => {
                  setSelectedPackage(pkg);
                  toast({ title: 'Paket Seçildi', description: `${pkg.name} paketi seçildi`, duration: 2000 });
                }}
              >
                {index === lowestPriceIndex && venue.packages.length > 1 && pkg.price > 0 && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                    En Uygun
                  </div>
                )}
                {index === highestPriceIndex && venue.packages.length > 1 && pkg.price > 0 && index !== lowestPriceIndex && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                    En Popüler
                  </div>
                )}
                {selectedPackage?.name === pkg.name && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Seçili
                  </div>
                )}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-pink-600 transition-colors">{pkg.name}</h3>
                  {pkg.description && <p className="text-sm text-gray-500 mt-1">{pkg.description}</p>}
                </div>
                {pkg.price > 0 ? (
                  <div className="mb-6">
                    {venue.activeCampaigns && venue.activeCampaigns.length > 0 ? (
                      <>
                        <div className="mb-2 bg-gradient-to-r from-red-500 to-pink-600 text-white px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1 animate-pulse">
                          {venue.activeCampaigns[0].discountType === 'percentage' 
                            ? `%${venue.activeCampaigns[0].discountValue} İndirim!`
                            : `₺${venue.activeCampaigns[0].discountValue?.toLocaleString('tr-TR')} İndirim!`}
                        </div>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-xl text-gray-400 line-through">₺{pkg.price?.toLocaleString('tr-TR')}</span>
                          <span className="text-3xl font-extrabold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                            ₺{(venue.activeCampaigns[0].discountType === 'percentage'
                              ? Math.round(pkg.price * (1 - venue.activeCampaigns[0].discountValue / 100))
                              : Math.max(0, pkg.price - venue.activeCampaigns[0].discountValue)
                            )?.toLocaleString('tr-TR')}
                          </span>
                          <span className="text-gray-500 text-sm">/paket</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-extrabold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">₺{pkg.price?.toLocaleString('tr-TR')}</span>
                        <span className="text-gray-500 text-sm">/paket</span>
                      </div>
                    )}
                    {pkg.minPerson && <p className="text-xs text-gray-400 mt-1">Minimum {pkg.minPerson} kişi</p>}
                  </div>
                ) : (
                  <div className="mb-6">
                    <span className="text-lg font-semibold text-gray-600">Fiyat için arayın</span>
                  </div>
                )}
                {pkg.features && pkg.features.length > 0 && (
                  <div className="space-y-2 bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Dahil Olan Hizmetler ({pkg.features.length})</p>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {pkg.features.map((feature, fIndex) => (
                        <div key={fIndex} className="flex items-center gap-3 text-sm text-gray-700 bg-white/50 rounded-lg p-2 hover:bg-white transition-colors">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span className="font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button className={`w-full mt-4 py-3 rounded-xl font-semibold transition-all ${
                  selectedPackage?.name === pkg.name
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
                  {selectedPackage?.name === pkg.name ? 'Bu Paket Seçildi' : 'Bu Paketi Seç'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

VenuePackages.displayName = 'VenuePackages';
export default VenuePackages;
